/**
 * Backend API client
 * Following NEW_SITE_CONSTITUTION_GUIDE.md Section 4.8
 */
import { signOut } from 'aws-amplify/auth';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { getAccessToken } from './tokenResolver';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const BDANCE_API_ORIGIN = (
  import.meta.env.VITE_BRAINDANCE_API_URL ||
  import.meta.env.VITE_BDANCE_API_URL ||
  'https://api.braindance.net'
)
  .replace(/\/+$/, '')
  .replace(/\/api$/, '');
const SITE_ID = (import.meta.env.VITE_SITE_ID as string | undefined) || 'promptfab';
const PERSONAWIZ_API_URL = 'https://personawiz.com/api/v1';
const DYNAMICBOOKS_API_URL = 'https://api.dynamicbooks.net/api/v1';
const STORE_API_URL = import.meta.env.VITE_STORE_API_URL || 'https://store-api.braindance.net/api';

export const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

export const bdanceApi = axios.create({
  baseURL: BDANCE_API_ORIGIN,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: false,
});

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  const token = await getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

bdanceApi.interceptors.request.use(async (config) => {
  const token = await getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-logout on 401
let isSigningOut = false;

type RetryableConfig = InternalAxiosRequestConfig & {
  _retry401?: boolean;
};

function redirectToLoginSafely(): void {
  const path = window.location.pathname;
  if (path === '/auth/callback') {
    return;
  }
  // Include ?logout=1 so App.tsx won't immediately redirect back to dashboard
  // even if the useAuth() hook hasn't cleared its state yet.
  window.location.replace('/login?logout=1');
}

async function handleUnauthorized(error: unknown): Promise<never> {
  const axiosError = error as AxiosError;
  const originalRequest = axiosError?.config as RetryableConfig | undefined;
  const status =
    typeof error === 'object' && error !== null && 'response' in error
      ? (error as { response?: { status?: number } }).response?.status
      : undefined;

  // Long-running actions can cross token-expiry boundaries.
  // Retry once with a forced refresh before treating this as a hard auth failure.
  if (status === 401 && originalRequest && !originalRequest._retry401) {
    originalRequest._retry401 = true;
    const refreshedToken = await getAccessToken(true);
    if (refreshedToken) {
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${refreshedToken}`;
      return api.request(originalRequest);
    }
  }

  if (status === 401 && !isSigningOut) {
    isSigningOut = true;
    try {
      await signOut();
    } catch {
      // ignore signout failures and still navigate to login
    } finally {
      redirectToLoginSafely();
      isSigningOut = false;
    }
  }

  return Promise.reject(error);
}

api.interceptors.response.use(
  (response) => response,
  handleUnauthorized
);

bdanceApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const axiosError = error as AxiosError;
    const originalRequest = axiosError?.config as RetryableConfig | undefined;
    const status =
      typeof error === 'object' && error !== null && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined;

    if (status === 401 && originalRequest && !originalRequest._retry401) {
      originalRequest._retry401 = true;
      const refreshedToken = await getAccessToken(true);
      if (refreshedToken) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${refreshedToken}`;
        return bdanceApi.request(originalRequest);
      }
    }

    return handleUnauthorized(error);
  }
);

export interface UserProfile {
  PK: string;
  SK: string;
  email: string;
  displayName?: string;
  xHandle?: string;
  publicProfile?: boolean;
  persona?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SubscriptionInfo {
  hasSubscription: boolean;
  tier: string;
  status: string;
  stripe_subscription_id?: string;
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end?: boolean;
  monthly_tokens?: number;
  site_entitlements?: string[];
  plan_id?: string;
  plan_name?: string;
}

export interface BillingInvoice {
  id: string;
  amount_paid: number;
  currency: string;
  status: string;
  invoice_pdf?: string;
  hosted_invoice_url?: string;
  period_start?: string;
  period_end?: string;
  created_at?: string;
}

export interface EntitlementResponse {
  site_id?: string;
  has_access?: boolean;
  subscription_status: string;
  site_entitlements?: string[];
  is_active?: boolean;
}

export interface CheckoutRequest {
  price_id: string;
  success_url: string;
  cancel_url: string;
  mode: 'subscription' | 'payment';
}

export interface CheckoutResponse {
  checkout_url: string;
  session_id: string;
}

export interface PortalResponse {
  portal_url: string;
}

export interface TokenBalanceCheckRequest {
  estimated_tokens: number;
  action: string;
}

export interface TokenBalanceCheckResponse {
  can_proceed: boolean;
  balance: number;
  remaining_after: number;
}

export interface TokenBalanceResponse {
  balance: number;
}

export interface TokenUsageSummaryResponse {
  success: boolean;
  data: {
    user_id: string;
    period: string;
    balance: number;
    total_used: number;
    total_purchased: number;
    total_input_tokens: number;
    total_output_tokens: number;
    total_requests: number;
    daily_breakdown?: Array<{ date: string; tokens: number }>;
    by_site?: Record<string, { tokens: number; requests: number }>;
  };
}

export const userProfileApi = {
  get: () => bdanceApi.get('/api/user/profile').then((r) => r.data as UserProfile),
  update: (data: Pick<UserProfile, 'displayName' | 'xHandle' | 'publicProfile' | 'persona'>) =>
    bdanceApi.put('/api/user/profile', data).then((r) => r.data as UserProfile),
};

export const billingApi = {
  getSubscription: () =>
    bdanceApi.get('/api/billing/subscription').then((r) => r.data as SubscriptionInfo),
  getInvoices: (limit = 10) =>
    bdanceApi.get('/api/billing/invoices', { params: { limit } }).then((r) => r.data as { invoices: BillingInvoice[] }),
  getEntitlements: (siteId?: string) =>
    bdanceApi.get('/api/billing/entitlements', { params: siteId ? { site_id: siteId } : {} }).then((r) => r.data as EntitlementResponse),
  createCheckout: (data: CheckoutRequest) =>
    bdanceApi.post('/api/billing/checkout', data).then((r) => r.data as CheckoutResponse),
  createPortal: (return_url: string) =>
    bdanceApi.post('/api/billing/portal', { return_url }).then((r) => r.data as PortalResponse),
};

export const tokenUsageApi = {
  checkBalance: async (data: TokenBalanceCheckRequest) => {
    const summary = await bdanceApi
      .get('/tokens/usage/summary', {
        params: { period: 'current_month' },
        headers: { 'X-Site-Id': SITE_ID },
      })
      .then((r) => r.data as TokenUsageSummaryResponse);

    const balance = summary?.data?.balance ?? 0;
    const estimated = Number.isFinite(data.estimated_tokens) ? data.estimated_tokens : 0;

    return {
      can_proceed: balance >= estimated,
      balance,
      remaining_after: Math.max(0, balance - estimated),
    } as TokenBalanceCheckResponse;
  },
  getBalance: async () => {
    const summary = await bdanceApi
      .get('/tokens/usage/summary', {
        params: { period: 'current_month' },
        headers: { 'X-Site-Id': SITE_ID },
      })
      .then((r) => r.data as TokenUsageSummaryResponse);

    return {
      balance: summary?.data?.balance ?? 0,
    } as TokenBalanceResponse;
  },
  getUsageSummary: (period = 'current_month') =>
    bdanceApi
      .get('/tokens/usage/summary', {
        params: { period },
        headers: { 'X-Site-Id': SITE_ID },
      })
      .then((r) => r.data as TokenUsageSummaryResponse),
};

// API functions
export const promptsApi = {
  list: (includePublic = false, limit?: number) =>
    api.get('/v1/prompts', { params: { includePublic, ...(limit ? { limit } : {}) } }).then((r) => r.data),
  get: (id: string) => api.get(`/v1/prompts/${id}`).then((r) => r.data),
  create: (data: Record<string, unknown>) => api.post('/v1/prompts', data).then((r) => r.data),
  update: (id: string, data: Record<string, unknown>) =>
    api.put(`/v1/prompts/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/v1/prompts/${id}`),
};

export const templatesApi = {
  list: () => api.get('/v1/templates').then((r) => r.data),
  get: (id: string) => api.get(`/v1/templates/${id}`).then((r) => r.data),
  create: (data: Record<string, unknown>) => api.post('/v1/templates', data).then((r) => r.data),
  update: (id: string, data: Record<string, unknown>) =>
    api.put(`/v1/templates/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/v1/templates/${id}`),
};

export interface SmartPromptResult {
  smartPrompt: string;
  suggestedTitle: string;
  promptSections?: {
    role?: string;
    task?: string;
    context?: string;
    constraints?: string;
    outputFormat?: string;
    examples?: string;
  };
  transformationNotes: string;
  estimatedEffectivenessBoost: string;
}

export const paiqApi = {
  generateSmartPrompt: (originalPrompt: string, context?: string) =>
    api
      .post<{ data: SmartPromptResult }>('/v1/paiq/generate-smart-prompt', { originalPrompt, context })
      .then((r) => r.data),
  calculateIQ: async (prompt: string) => {
    try {
      return await api.post('/v1/paiq/calculate-iq', { prompt }).then((r) => r.data);
    } catch (error) {
      console.error('calculate-iq failed, using client fallback:', error);

      const trimmed = prompt.trim();
      const hasStructure = /\n|-|\d\.|:/.test(trimmed);
      const hasConstraints = /must|should|avoid|do not|don't|required|constraints?/i.test(trimmed);
      const hasContext = /context|audience|goal|objective|background/i.test(trimmed);

      let score = 21;
      if (trimmed.length > 250) score += 3;
      if (hasStructure) score += 2;
      if (hasConstraints) score += 2;
      if (hasContext) score += 1;
      if (score > 35) score = 35;

      let grade = 'Adequate';
      if (score >= 30) grade = 'Excellent';
      else if (score >= 26) grade = 'Good';

      return {
        data: {
          paiqScore: score,
          maxScore: 35,
          paiqGrade: grade,
          dimensions: {
            firstPrinciplesDepth: { score: hasContext ? 4 : 3, reasoning: 'Fallback heuristic from prompt grounding/context.' },
            cognitiveConstraints: { score: hasConstraints ? 4 : 3, reasoning: 'Constraint language improves controllability.' },
            crossDomainSynthesis: { score: hasContext ? 4 : 3, reasoning: 'Context cues suggest cross-domain grounding.' },
            specificity: { score: hasStructure ? 4 : 3, reasoning: 'Structured formatting improves specificity.' },
            signalToNoise: { score: trimmed.length > 120 ? 4 : 3, reasoning: 'Prompt detail estimated from content density.' },
            actionability: { score: hasConstraints ? 4 : 3, reasoning: 'Directive language suggests actionable output.' },
            critiqueLoop: { score: /review|audit|critique|verify|check/i.test(trimmed) ? 4 : 3, reasoning: 'Self-check cues improve reliability.' },
          },
          strengths: [
            'Client fallback kept analysis workflow operational.',
            hasStructure
              ? 'Prompt contains structural signals that aid model compliance.'
              : 'Prompt can benefit from clearer structural sections.',
          ],
          weaknesses: [
            'This result is heuristic because server-side analysis was unavailable.',
            'Add explicit constraints and measurable success criteria.',
          ],
          improvements: [
            'Add a clear role, audience, and objective statement.',
            'Use bullet points for constraints and output format.',
            'Include a short self-check step before final answer generation.',
          ],
          rewrittenPrompt: `ROLE: Expert assistant\n\nTASK:\n${trimmed}\n\nCONTEXT:\n- Audience: [define]\n- Goal: [define]\n\nCONSTRAINTS:\n- Must provide specific, actionable guidance\n- Must include assumptions and risks\n- Do not use vague or generic language\n\nOUTPUT FORMAT:\n1. Summary\n2. Detailed response\n3. Risks and mitigations\n4. Next actions`,
        },
      };
    }
  },
  generatePrePrompt: (initialPrompt: string, personaDescription?: string, systemPrompt?: string, outputFormat?: string) =>
    api.post('/v1/paiq/generate-pre-prompt', { initialPrompt, personaDescription, systemPrompt, outputFormat }).then((r) => r.data),
  generateFinalPrompt: (prePrompt: string) =>
    api.post('/v1/paiq/generate-final-prompt', { prePrompt }).then((r) => r.data),
};

export interface SemanticExtraction {
  chapterTitle: string;
  centralThesis: string;
  rootConcept: string;
  majorBranches: Array<{
    branch: string;
    subBranches: string[];
  }>;
}

export interface SemanticTreeResult {
  semanticExtraction: SemanticExtraction;
  finalImagePrompt: string;
  negativePrompt: string;
  improvedPrompt: string;
  paiqScore?: number;
  paiqGrade?: string;
}

export const semanticTreeApi = {
  generate: (sourceText: string) =>
    api.post('/v1/semantic-tree/generate', { sourceText }).then((r) => r.data as { data: SemanticTreeResult }),
};

export interface MarketingPackResult {
  marketingPlan: string;
  productName: string;
  targetAudience: string;
  positioningStatement: string;
  assetCount: number;
  paiqScore?: number;
  paiqGrade?: string;
}

export const marketingPackApi = {
  generate: (productDescription: string) =>
    api
      .post('/v1/marketing-pack/generate', { productDescription })
      .then((r) => r.data as { data: MarketingPackResult }),
};

export const packsApi = {
  list: (includePublic = false) =>
    api.get('/v1/packs', { params: { includePublic } }).then((r) => r.data),
  ensureFromBook: (bookId: string, promptPack?: unknown, projectName?: string) =>
    api.post('/v1/packs/ensure-from-book', { bookId, promptPack, ...(projectName ? { projectName } : {}) }).then((r) => r.data),
  syncFromBook: (bookId: string) =>
    api.get('/v1/packs/sync-from-book', { params: { bookId } }).then((r) => r.data),
  get: (id: string) => api.get(`/v1/packs/${id}`).then((r) => r.data),
  create: (data: Record<string, unknown>) => api.post('/v1/packs', data).then((r) => r.data),
  update: (id: string, data: Record<string, unknown>) =>
    api.put(`/v1/packs/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/v1/packs/${id}`),
  getContents: (id: string) => api.get(`/v1/packs/${id}/contents`).then((r) => r.data),
  addPrompts: (id: string, promptIds: string[]) =>
    api.post(`/v1/packs/${id}/prompts`, { promptIds }).then((r) => r.data),
  removePrompts: (id: string, promptIds: string[]) =>
    api.delete(`/v1/packs/${id}/prompts`, { data: { promptIds } }).then((r) => r.data),
  generate: (id: string, format: 'json' | 'zip' = 'json') =>
    api.post(`/v1/packs/${id}/generate`, { format }).then((r) => r.data),
  importText: (payload: {
    text?: string;
    prompts?: Array<{ name: string; content: string; category?: string; tags?: string[] }>;
    packName: string;
    description: string;
    tags: string[];
    isPublic: boolean;
  }) => api.post('/v1/packs/import-text', payload).then((r) => r.data),
  extractText: (payload: { text: string }) =>
    api.post('/v1/packs/extract-text', payload).then((r) => r.data) as Promise<{
      data: {
        packName?: string;
        description?: string;
        suggestedTags?: string[];
        prompts: Array<{ name: string; content: string; category?: string; tags?: string[] }>;
      };
    }>,
};

export const versionsApi = {
  list: (promptId: string) =>
    api.get('/v1/versions', { params: { promptId } }).then((r) => r.data),
  get: (promptId: string, versionId: string) =>
    api.get(`/v1/versions/${promptId}/${versionId}`).then((r) => r.data),
  getLatest: (promptId: string) =>
    api.get(`/v1/versions/${promptId}/latest`).then((r) => r.data),
  create: (data: { promptId: string; content: string; label?: string; paiqScore?: number }) =>
    api.post('/v1/versions', data).then((r) => r.data),
  delete: (promptId: string, versionId: string) =>
    api.delete(`/v1/versions/${promptId}/${versionId}`),
  compare: (promptId: string, versionId1: string, versionId2: string) =>
    api.post('/v1/versions/compare', { promptId, versionId1, versionId2 }).then((r) => r.data),
  restore: (promptId: string, versionId: string) =>
    api.post(`/v1/versions/${promptId}/${versionId}/restore`).then((r) => r.data),
};

export const projectsApi = {
  list: () => api.get('/v1/projects').then((r) => r.data),
  get: (id: string) => api.get(`/v1/projects/${id}`).then((r) => r.data),
  byBook: (bookId: string) => api.get(`/v1/projects/by-book/${encodeURIComponent(bookId)}`).then((r) => r.data),
  create: (data: { name: string; description?: string }) =>
    api.post<{ data: { entityId: string; projectName: string } }>('/v1/projects', data).then((r) => r.data),
  delete: (id: string) => api.delete(`/v1/projects/${id}`),
  update: (id: string, data: { name?: string; description?: string }) =>
    api.put(`/v1/projects/${id}`, data).then((r) => r.data),
  importJson: (json: Record<string, unknown>) =>
    api.post<{ data: { projectId: string; projectName: string; packsCreated: number; promptsCreated: number } }>(
      '/v1/projects/import-json',
      json
    ).then((r) => r.data),
  optimizeAllPrompts: (
    id: string,
    data?: { overwriteUserPrompt?: boolean; cursor?: string; limit?: number; action?: 'stage' | 'commit' | 'clear' }
  ) =>
    api.post(`/v1/projects/${id}/optimize-all-prompts`, data ?? {}).then((r) => r.data),
  generateVersion: (
    id: string,
    data: {
      mode: 'default-from-book' | 'create-new-persona-version';
      personaText?: string;
      generationPrompt?: string;
    }
  ) => api.post(`/v1/projects/${id}/generate-version`, data).then((r) => r.data),
  publishPack: (
    id: string,
    data: { priceInCents: number; parentProductId?: string }
  ) =>
    api.post<{ data: { productId: string; alreadyExists: boolean; message?: string } }>(
      `/v1/projects/${id}/publish-pack`,
      data
    ).then((r) => r.data),
};

/* ------------------------------------------------------------------ */
/*  Public Library                                                    */
/* ------------------------------------------------------------------ */

const LIBRARY_BASE_URL = (
  (import.meta.env.VITE_LIBRARY_BASE_URL as string | undefined) || 'https://library.promptfab.ai'
).replace(/\/+$/, '');

export type LibraryItemType = 'prompt' | 'pack' | 'project';

export interface LibraryPaiqDimensionScore {
  score: number;
  reasoning: string;
}
export interface LibraryPaiqAnalysis {
  paiqScore: number;
  maxScore: number;
  paiqGrade: string;
  dimensions?: Record<string, LibraryPaiqDimensionScore | undefined>;
  strengths?: string[];
  weaknesses?: string[];
  improvements?: string[];
  rewrittenPrompt?: string;
}

export interface LibraryBrowseEntry {
  code: string;
  type: LibraryItemType;
  title: string;
  description: string;
  tags: string[];
  category?: string;
  paiqScore?: number;
  ownerName?: string;
  parentTitle?: string;
  contentHash: string;
  ratingAvg: number;
  ratingCount: number;
  favoriteCount: number;
  publishedAt: string;
}

export interface LibraryPromptContent {
  promptText: string;
  systemPrompt?: string;
  outputFormat?: string;
  personaRoleContext?: string;
}
export interface LibraryPackContent {
  prompts: Array<{ name: string; description?: string; promptText: string }>;
}
export interface LibraryProjectContent {
  bookTitle?: string;
  bookAuthor?: string;
  chapters: Array<{
    chapterIndex: number;
    chapterTitle: string;
    prompts: Array<{ name: string; description?: string; promptText: string }>;
  }>;
}

export interface LibraryBlob {
  code: string;
  type: LibraryItemType;
  title: string;
  description: string;
  tags: string[];
  category?: string;
  paiqScore?: number;
  paiqAnalysis?: LibraryPaiqAnalysis;
  ownerName?: string;
  parent?: { type: 'pack' | 'project'; sourceId: string; title: string };
  publishedAt: string;
  content: LibraryPromptContent | LibraryPackContent | LibraryProjectContent;
}

export interface LibraryItemDetail extends LibraryBrowseEntry {
  viewCount: number;
  landingUrl: string;
  paiqAnalysis?: LibraryPaiqAnalysis;
  yourRating: number;
  favorited: boolean;
  isOwner: boolean;
}

export const libraryApi = {
  /** Static search corpus fetched directly from the R2 CDN (no auth). */
  getIndex: async (): Promise<LibraryBrowseEntry[]> => {
    const res = await fetch(`${LIBRARY_BASE_URL}/index.json`, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`Failed to load library index (${res.status})`);
    return (await res.json()) as LibraryBrowseEntry[];
  },
  /** Immutable content blob fetched directly from the R2 CDN (no auth). */
  getContent: async (contentHash: string): Promise<LibraryBlob> => {
    const res = await fetch(`${LIBRARY_BASE_URL}/blobs/${contentHash}.json`);
    if (!res.ok) throw new Error(`Failed to load library content (${res.status})`);
    return (await res.json()) as LibraryBlob;
  },
  publish: (payload: {
    type: LibraryItemType;
    sourceId: string;
    title?: string;
    description?: string;
    tags?: string[];
    category?: string;
    includePaiqAnalysis?: boolean;
  }) =>
    api.post('/v1/library/publish', payload).then(
      (r) =>
        r.data as {
          data: {
            code: string;
            landingUrl: string;
            contentHash: string;
            updated: boolean;
            /**
             * Populated when a publish fans out into multiple library items
             * (currently only when publishing a project — each prompt in the
             * project becomes its own library entry).
             */
            itemsPublished?: number;
            items?: Array<{ code: string; landingUrl: string; title: string; updated: boolean }>;
          };
        },
    ),
  listMine: () =>
    api.get('/v1/library/mine').then((r) => r.data as { data: LibraryBrowseEntry[] }),
  listFavorites: () =>
    api.get('/v1/library/favorites').then((r) => r.data),
  getDetail: (code: string) =>
    api.get(`/v1/library/${code}`).then((r) => r.data as { data: LibraryItemDetail }),
  rate: (code: string, stars: number) =>
    api
      .post(`/v1/library/${code}/rate`, { stars })
      .then((r) => r.data as { data: { ratingAvg: number; ratingCount: number; yourRating: number } }),
  favorite: (code: string) =>
    api
      .post(`/v1/library/${code}/favorite`)
      .then((r) => r.data as { data: { favorited: boolean; favoriteCount: number } }),
  trackView: (code: string) =>
    api.post(`/v1/library-public/${code}/view`).catch(() => undefined),
  unpublish: (code: string) => api.delete(`/v1/library/${code}`),
};

/**
 * PersonaWiz external API client
 * Uses access token for cross-site API calls to PersonaWiz
 */
export interface PersonaSummary {
  id: string;
  personaName: string;
  userRole?: string;
  useCase?: string;
  description?: string;
  createdAt: string;
}

export const personaWizApi = {
  /**
   * List user's personas from PersonaWiz
   * Uses access token for cross-site API authentication
   */
  listPersonas: async (userId: string): Promise<{ data: PersonaSummary[] }> => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');

    const response = await axios.get(`${PERSONAWIZ_API_URL}/external/personas`, {
      params: { userId },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  /**
   * Get full persona details from PersonaWiz
   * Uses access token for cross-site API authentication
   */
  getPersona: async (personaId: string, userId: string): Promise<{ data: Record<string, unknown> }> => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');

    const response = await axios.get(`${PERSONAWIZ_API_URL}/external/personas/${personaId}`, {
      params: { userId },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

/**
 * DynamicBooks external API client
 * For fetching user's authored books
 */
export interface BookSummary {
  bookId: string;
  title: string;
  author?: string;
  status: string;
  coverImageUrl?: string;
  contentFolder?: string;
  createdAt: string;
  updatedAt?: string;
}

export const dynamicBooksApi = {
  /**
   * List user's authored books from DynamicBooks
   * Uses access token for API authentication (same Cognito pool)
   */
  listAuthoredBooks: async (): Promise<{ data: BookSummary[] }> => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');

    const response = await axios.get(`${DYNAMICBOOKS_API_URL}/books/authored`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  /**
   * Get prompt-pack payload for a specific book from DynamicBooks
   */
  getPromptPack: async (bookId: string): Promise<unknown> => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');

    const response = await axios.get(`${DYNAMICBOOKS_API_URL}/books/${encodeURIComponent(bookId)}/prompt-pack`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

/**
 * BrainDance Store API client
 * For publishing products to the store catalog
 */
export interface PublishSKURequest {
  bookId: string;
  authorId: string;
  productType: 'dynamic-book' | 'prompt-pack' | 'audiobook' | 'album' | 'song';
  priceInCents?: number;
  parentProductId?: string;
}

export interface PublishSKUResponse {
  message: string;
  productId: string;
  productType: string;
  name: string;
}

export const storeApi = {
  /**
   * Publish a product (prompt pack) to the BrainDance store
   */
  publishSKU: async (data: PublishSKURequest): Promise<PublishSKUResponse> => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');

    // Debug: log token claims
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      console.log('[storeApi] Token claims:', {
        iss: payload.iss,
        client_id: payload.client_id,
        sub: payload.sub,
        exp: new Date(payload.exp * 1000).toISOString(),
      });
      console.log('[storeApi] Request authorId:', data.authorId);
      console.log('[storeApi] authorId matches sub:', data.authorId === payload.sub);
    } catch (e) {
      console.error('[storeApi] Failed to decode token:', e);
    }

    const response = await axios.post(`${STORE_API_URL}/publish-sku`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },
};
